import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Button, Paper, Grid, Chip, Divider, List, ListItem, 
  ListItemText, ListItemAvatar, Avatar, TextField, IconButton, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Stepper, Step, StepLabel
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function ContractDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [newDraftFile, setNewDraftFile] = useState(null);
  const [status, setStatus] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchContract = async () => {
    try {
      const res = await axios.get(`${API_URL}/contracts/${id}`, { headers });
      setContract(res.data);
      setStatus(res.data.status);
    } catch (err) {
      console.error(err);
      alert('Failed to load contract details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContract();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
      try {
          await axios.put(`${API_URL}/contracts/${id}`, { status: newStatus }, { headers });
          setStatus(newStatus);
          fetchContract(); // Refresh to get updated history if we add that later
      } catch (err) {
          console.error(err);
          alert('Failed to update status');
      }
  };

  const handleAddComment = async () => {
      if (!newComment.trim()) return;
      try {
          await axios.post(`${API_URL}/contracts/${id}/comments`, { comment: newComment }, { headers });
          setNewComment('');
          fetchContract();
      } catch (err) {
          console.error(err);
          alert('Failed to add comment');
      }
  };

  const handleUploadDraft = async () => {
      if (!newDraftFile) return;
      try {
          const formData = new FormData();
          formData.append('contract_pdf', newDraftFile);
          
          await axios.post(`${API_URL}/contracts/${id}/drafts`, formData, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' }
          });
          setNewDraftFile(null);
          fetchContract();
      } catch (err) {
          console.error(err);
          alert('Failed to upload draft');
      }
  };

  const handleDownload = (draftId, filename) => {
      const encodedFilename = encodeURIComponent(filename);
      const url = `${API_URL}/contracts/${id}/files/${encodedFilename}`;
      window.open(url, '_blank');
  };

  if (loading) return <Loading />;
  if (!contract) return <Typography>Contract not found</Typography>;

  const steps = [
      { label: 'New', icon: <NoteAddIcon /> },
      { label: 'In Progress', icon: <PendingActionsIcon /> },
      { label: 'Done by Client', icon: <TaskAltIcon /> },
      { label: 'Signed', icon: <VerifiedUserIcon /> }
  ];

  const activeStep = steps.findIndex(s => s.label === status);

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/contracts')} sx={{ mb: 2 }}>
        Back to Contracts
      </Button>

      {/* Workflow Cycle - Full Width */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Workflow Cycle</Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((step) => (
                        <Step key={step.label}>
                            <StepLabel StepIconComponent={() => (
                                <Avatar 
                                    sx={{ 
                                        bgcolor: activeStep >= steps.indexOf(step) ? 'primary.main' : 'grey.300',
                                        color: 'white',
                                        width: 40, height: 40
                                    }}
                                >
                                    {step.icon}
                                </Avatar>
                            )}>
                                {step.label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            <Box>
                {activeStep < 3 ? (
                    <Button 
                        variant="contained" 
                        color="primary"
                        endIcon={<ArrowForwardIcon />}
                        sx={{ borderRadius: 2, px: 3, textTransform: 'none', fontWeight: 'bold', minWidth: '120px' }}
                        onClick={() => {
                            const nextIndex = activeStep + 1;
                            if (nextIndex < steps.length) handleStatusChange(steps[nextIndex].label);
                        }}
                    >
                        Next
                    </Button>
                ) : (
                    <Chip label="Completed" color="success" icon={<VerifiedUserIcon />} />
                )}
            </Box>
          </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column: Details & Drafts */}
        <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h5" fontWeight="bold">Contract Details</Typography>
                    <Chip label={contract.status} color={contract.status === 'Signed' ? 'success' : 'primary'} />
                </Box>
                
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Contract ID</Typography>
                        <Typography variant="body1">#{contract.id}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Created By</Typography>
                        <Typography variant="body1">{contract.created_by}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Client</Typography>
                        <Typography variant="body1">{contract.client_name}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Project</Typography>
                        <Typography variant="body1">{contract.project_name || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">Location</Typography>
                        <Typography variant="body1">{contract.country} - {contract.division}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography variant="subtitle2" color="textSecondary">DDP</Typography>
                        <Typography variant="body1">{contract.ddp}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{contract.description}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Draft Versions</Typography>
                <List>
                    {contract.drafts.map((draft) => (
                        <ListItem key={draft.id} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                            <ListItemAvatar>
                                <Avatar sx={{ bgcolor: 'primary.light' }}>
                                    <DescriptionIcon />
                                </Avatar>
                            </ListItemAvatar>
                            <ListItemText 
                                primary={`Version ${draft.version}`} 
                                secondary={`Uploaded by ${draft.created_by} on ${draft.created_at}`} 
                            />
                            <Button 
                                startIcon={<DownloadIcon />} 
                                onClick={() => handleDownload(draft.id, draft.file_path)}
                            >
                                Download
                            </Button>
                        </ListItem>
                    ))}
                </List>
                
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Upload New Version</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="outlined" component="label" fullWidth>
                            {newDraftFile ? newDraftFile.name : "Select PDF File"}
                            <input 
                                type="file" 
                                hidden 
                                accept="application/pdf" 
                                onChange={e => setNewDraftFile(e.target.files[0])} 
                            />
                        </Button>
                        <Button 
                            variant="contained" 
                            disabled={!newDraftFile}
                            onClick={handleUploadDraft}
                            startIcon={<UploadIcon />}
                        >
                            Upload
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Grid>

        {/* Right Column: Status & Comments */}
        <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', height: 'fit-content' }}>
                <CardContent>
                    <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>Comments</Typography>
                    <List sx={{ maxHeight: 400, overflow: 'auto', mb: 2 }}>
                        {contract.comments.map((comment) => (
                            <ListItem key={comment.id} alignItems="flex-start" sx={{ px: 0 }}>
                                <ListItemAvatar>
                                    <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                                        {comment.user.charAt(0)}
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <Typography variant="subtitle2">{comment.user}</Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {new Date(comment.created_at).toLocaleDateString()}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={comment.comment}
                                />
                            </ListItem>
                        ))}
                        {contract.comments.length === 0 && (
                            <Typography variant="body2" color="textSecondary" align="center">No comments yet.</Typography>
                        )}
                    </List>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Add a comment..."
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
                        />
                        <IconButton color="primary" onClick={handleAddComment} disabled={!newComment.trim()}>
                            <SendIcon />
                        </IconButton>
                    </Box>
                </CardContent>
            </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ContractDetails;
